import React from "react";
import { motion } from "framer-motion";
import { MapPin, GraduationCap, ArrowUpRight, Sparkles } from "lucide-react";
import { EDUCATION } from "./data";
import { resolveFileUrl } from "./fileUrl";

const SchoolCard = ({ school, idx }) => {
  const gallery = school.gallery && school.gallery.length > 1 ? school.gallery : null;
  const [activeImg, setActiveImg] = React.useState(school.image);

  React.useEffect(() => { setActiveImg(school.image); }, [school.image]);

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true, margin: "-80px" }}
      className="relative grid lg:grid-cols-12 gap-10 items-center"
    >
      {/* Visual */}
      <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
        <div className="relative aspect-[4/3] rounded-[24px] overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-xl group">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle at 30% 30%, ${school.accent}40, transparent 50%), radial-gradient(circle at 70% 70%, ${school.accent}20, transparent 50%)`,
            }}
          />
          {activeImg ? (
            <>
              <img
                key={activeImg}
                src={resolveFileUrl(activeImg)}
                alt={school.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </>
          ) : (
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <GraduationCap className="w-12 h-12 mx-auto text-white/30" />
                <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-white/40">Campus Visual</div>
              </div>
            </div>
          )}
          <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-white/70 font-mono flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: school.accent }} />
            Institution · {String(idx + 1).padStart(2, "0")}
          </div>
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
            <div className="text-white/80 text-[12px] flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" /> {school.location}
            </div>
            <div className="text-white/80 text-[12px] font-mono">{school.span}</div>
          </div>
        </div>

        {/* Thumbnail gallery */}
        {gallery && (
          <div className="mt-4 flex items-center gap-3">
            {gallery.map((g, i) => (
              <button
                key={g + i}
                onClick={() => setActiveImg(g)}
                className={`relative h-[68px] w-[100px] rounded-lg overflow-hidden border transition-all ${
                  activeImg === g
                    ? "border-white/60 scale-[1.03]"
                    : "border-white/10 opacity-70 hover:opacity-100 hover:border-white/30"
                }`}
                style={activeImg === g ? { boxShadow: `0 0 0 2px ${school.accent}55` } : undefined}
              >
                <img src={resolveFileUrl(g)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/30 font-mono ml-1">
              {gallery.length} frames
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
        <div className="text-[11px] tracking-[0.4em] uppercase font-mono" style={{ color: school.accent }}>
          {school.program}
        </div>
        <h3
          className="mt-3 text-[34px] md:text-[44px] leading-[1.05] tracking-tight text-white"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {school.name}
        </h3>
        <p className="mt-5 text-[15px] md:text-[16px] leading-[1.75] text-white/60">
          {school.description}
        </p>
        <ul className="mt-6 space-y-2">
          {school.highlights.map((h) => (
            <li key={h} className="flex items-start gap-3 text-[14px] text-white/70">
              <Sparkles className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: school.accent }} />
              {h}
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
};

const Education = () => {
  return (
    <section id="education" className="relative py-28 px-6 md:px-10">
      <div className="max-w-[1300px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono">04 / Education</div>
          <div className="mt-3 grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-[44px] md:text-[80px] leading-[0.95] tracking-tight text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Where I was <span className="italic">made</span>.
            </h2>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-md justify-self-end">
              Three institutions. One philosophy: that character compounds quietly, in the early mornings and the long evenings, long before it is ever measured.
            </p>
          </div>
        </motion.div>

        <div className="space-y-28">
          {EDUCATION.map((s, i) => (
            <SchoolCard key={s.id} school={s} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Education;
